import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Tag, 
  Button, 
  Space, 
  Statistic,
  Row,
  Col,
  message,
  Spin,
  Modal,
  DatePicker,
  Select,
  Input,
  Tooltip
} from 'antd';
import { 
  EyeOutlined, 
  StarOutlined,
  FormOutlined,
  DownloadOutlined,
  FilterOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminApi, SurveyResponseItem } from '../lib/api';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import type { RangePickerProps } from 'antd/es/date-picker';

const { RangePicker } = DatePicker;
const { Option } = Select;

const FEATURE_LABELS: Record<string, string> = {
  smart_add: "Smart Add Clothes",
  try_on: "Virtual Try-On",
  ai_recommendations: "AI Recommendations",
  photo_models: "Photo Models",
  batch_add: "Batch Add",
  ai_stylist: "AI Stylist Chat"
};

const USAGE_LABELS: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  rarely: "Rarely"
};

const MAIN_VALUE_LABELS: Record<string, string> = {
  save_time: "Saves time",
  discover_styles: "Discover styles",
  organize_wardrobe: "Organize wardrobe",
  recommendations: "Recommendations",
  try_before_buy: "Try before buy",
  other: "Other"
};

const CHALLENGE_LABELS: Record<string, string> = {
  too_complicated: "Too complicated",
  slow: "Too slow",
  missing_features: "Missing features",
  hard_to_find: "Hard to find",
  other: "Other"
};

const DEVICE_TYPE_LABELS: Record<string, string> = {
  mobile: "Mobile",
  tablet: "Tablet",
  desktop: "Desktop"
};

const Surveys: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState<SurveyResponseItem[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    recent: 0,
    avgRating: 0,
    favoriteFeatureCounts: {} as Record<string, number>,
    improveFeatureCounts: {} as Record<string, number>,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as Record<number, number>
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [selectedSurvey, setSelectedSurvey] = useState<SurveyResponseItem | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    dateRange: null as [Dayjs, Dayjs] | null,
    minRating: undefined as number | undefined,
    maxRating: undefined as number | undefined,
    deviceType: undefined as string | undefined
  });
  const [showFilters, setShowFilters] = useState(false);

  const calculateStats = (items: SurveyResponseItem[]) => {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const recent = items.filter(item => {
      const createdAt = new Date(item.created_at);
      return createdAt >= sevenDaysAgo;
    }).length;

    const ratings = items
      .map(item => item.rating)
      .filter((rating): rating is number => rating !== undefined && rating !== null);
    const avgRating = ratings.length > 0
      ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length
      : 0;

    // Feature counts
    const favoriteCounts: Record<string, number> = {};
    const improveCounts: Record<string, number> = {};
    const ratingDist: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    items.forEach(item => {
      item.favorite_features.forEach(f => {
        favoriteCounts[f] = (favoriteCounts[f] || 0) + 1;
      });
      item.least_favorite_features.forEach(f => {
        improveCounts[f] = (improveCounts[f] || 0) + 1;
      });
      if (item.rating) {
        ratingDist[item.rating] = (ratingDist[item.rating] || 0) + 1;
      }
    });

    setStats({
      total: items.length,
      recent,
      avgRating: Math.round(avgRating * 10) / 10,
      favoriteFeatureCounts: favoriteCounts,
      improveFeatureCounts: improveCounts,
      ratingDistribution: ratingDist
    });
  };

  const loadSurveys = async (page: number = 1, applyFilters: boolean = true) => {
    try {
      setLoading(true);
      const params: any = {
        limit: pagination.pageSize,
        offset: (page - 1) * pagination.pageSize,
        sort_by: 'created_at',
        sort_order: 'desc'
      };

      if (applyFilters) {
        if (filters.dateRange) {
          params.start_date = filters.dateRange[0].toISOString();
          params.end_date = filters.dateRange[1].toISOString();
        }
        if (filters.minRating !== undefined) {
          params.min_rating = filters.minRating;
        }
        if (filters.maxRating !== undefined) {
          params.max_rating = filters.maxRating;
        }
      }

      const response = await adminApi.getSurveys(params);

      setSurveys(response.items);
      setTotal(response.total);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: response.total
      }));

      // Calculate stats from all loaded items (for current page)
      calculateStats(response.items);
    } catch (error: any) {
      message.error('Failed to load surveys: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSurveys(1);
  }, []);

  const handleTableChange = (newPagination: any) => {
    loadSurveys(newPagination.current);
  };

  const handleViewDetails = (survey: SurveyResponseItem) => {
    setSelectedSurvey(survey);
    setModalVisible(true);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    loadSurveys(1, true);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: null,
      minRating: undefined,
      maxRating: undefined,
      deviceType: undefined
    });
    loadSurveys(1, false);
    setShowFilters(false);
  };

  const exportToCSV = () => {
    const headers = ['User ID', 'Username', 'Email', 'Rating', 'NPS', 'Usage', 'Favorite Features', 'Needs Improvement', 'Main Value', 'Biggest Challenge', 'Indispensable Feature', 'Additional Feedback', 'Device Type', 'Completion Time (s)', 'Submitted'];
    const rows = surveys.map(survey => [
      survey.user_id,
      survey.user_username || '',
      survey.user_email || '',
      survey.rating ?? '',
      survey.nps_score ?? '',
      survey.usage_frequency ? (USAGE_LABELS[survey.usage_frequency] || survey.usage_frequency) : '',
      survey.favorite_features.map(f => FEATURE_LABELS[f] || f).join('; '),
      survey.least_favorite_features.map(f => FEATURE_LABELS[f] || f).join('; '),
      (survey.main_value || []).map(v => MAIN_VALUE_LABELS[v] || v).join('; '),
      (survey.biggest_challenge || []).map(c => CHALLENGE_LABELS[c] || c).join('; '),
      (survey.indispensable_feature || '').replace(/"/g, '""'),
      (survey.additional_feedback || '').replace(/"/g, '""'),
      survey.device_type || '',
      survey.completion_time_seconds ?? '',
      new Date(survey.created_at).toISOString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `surveys_${dayjs().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Survey data exported to CSV');
  };

  const exportToJSON = () => {
    const jsonContent = JSON.stringify(surveys, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `surveys_${dayjs().format('YYYY-MM-DD')}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Survey data exported to JSON');
  };

  const renderStars = (rating?: number) => {
    if (!rating) return <span style={{ color: '#999' }}>No rating</span>;
    return (
      <Space>
        {[1, 2, 3, 4, 5].map(star => (
          <StarOutlined
            key={star}
            style={{
              color: star <= rating ? '#faad14' : '#d9d9d9',
              fontSize: '14px'
            }}
          />
        ))}
        <span>({rating})</span>
      </Space>
    );
  };

  const columns: ColumnsType<SurveyResponseItem> = [
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.user_username || record.user_email || 'Unknown'}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>
            {record.user_id.substring(0, 8)}...
          </div>
        </div>
      )
    },
    {
      title: 'Favorite Features',
      dataIndex: 'favorite_features',
      key: 'favorite_features',
      width: 200,
      render: (features: string[]) => (
        <Space size={[4, 4]} wrap>
          {features.length > 0 ? (
            features.map(feature => (
              <Tag key={feature} color="green">
                {FEATURE_LABELS[feature] || feature}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999' }}>None</span>
          )}
        </Space>
      )
    },
    {
      title: 'Needs Improvement',
      dataIndex: 'least_favorite_features',
      key: 'least_favorite_features',
      width: 200,
      render: (features: string[]) => (
        <Space size={[4, 4]} wrap>
          {features.length > 0 ? (
            features.map(feature => (
              <Tag key={feature} color="orange">
                {FEATURE_LABELS[feature] || feature}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999' }}>None</span>
          )}
        </Space>
      )
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 90,
      render: (rating?: number) => renderStars(rating)
    },
    {
      title: 'NPS',
      dataIndex: 'nps_score',
      key: 'nps_score',
      width: 60,
      render: (n?: number) => n != null ? <Tag color="blue">{n}</Tag> : <span style={{ color: '#999' }}>—</span>
    },
    {
      title: 'Usage',
      dataIndex: 'usage_frequency',
      key: 'usage_frequency',
      width: 90,
      render: (u?: string) => u ? (USAGE_LABELS[u] || u) : <span style={{ color: '#999' }}>—</span>
    },
    {
      title: 'Device',
      dataIndex: 'device_type',
      key: 'device_type',
      width: 100,
      render: (deviceType?: string) => (
        <Tag>{deviceType ? DEVICE_TYPE_LABELS[deviceType] || deviceType : 'Unknown'}</Tag>
      )
    },
    {
      title: 'Submitted',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <div className="page-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>User Surveys</h1>
        <Space>
          <Button
            icon={<FilterOutlined />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportToCSV}
          >
            Export CSV
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportToJSON}
          >
            Export JSON
          </Button>
        </Space>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ marginBottom: 8 }}>Date Range</div>
                <RangePicker
                  style={{ width: '100%' }}
                  value={filters.dateRange}
                  onChange={(dates) => handleFilterChange('dateRange', dates)}
                />
              </Col>
              <Col span={4}>
                <div style={{ marginBottom: 8 }}>Min Rating</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Any"
                  allowClear
                  value={filters.minRating}
                  onChange={(value) => handleFilterChange('minRating', value)}
                >
                  {[1, 2, 3, 4, 5].map(r => (
                    <Option key={r} value={r}>{r}</Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <div style={{ marginBottom: 8 }}>Max Rating</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Any"
                  allowClear
                  value={filters.maxRating}
                  onChange={(value) => handleFilterChange('maxRating', value)}
                >
                  {[1, 2, 3, 4, 5].map(r => (
                    <Option key={r} value={r}>{r}</Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <div style={{ marginBottom: 8 }}>Device Type</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Any"
                  allowClear
                  value={filters.deviceType}
                  onChange={(value) => handleFilterChange('deviceType', value)}
                >
                  <Option value="mobile">Mobile</Option>
                  <Option value="tablet">Tablet</Option>
                  <Option value="desktop">Desktop</Option>
                </Select>
              </Col>
              <Col span={4}>
                <div style={{ marginBottom: 8 }}>&nbsp;</div>
                <Space>
                  <Button type="primary" onClick={handleApplyFilters}>Apply</Button>
                  <Button onClick={handleClearFilters}>Clear</Button>
                </Space>
              </Col>
            </Row>
          </Space>
        </Card>
      )}

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Surveys"
              value={stats.total}
              prefix={<FormOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Recent (7 days)"
              value={stats.recent}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Average Rating"
              value={stats.avgRating}
              precision={1}
              prefix={<StarOutlined />}
              suffix="/ 5"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Responses"
              value={surveys.length}
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Analytics Visualizations */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="Top Favorite Features">
            <Space direction="vertical" style={{ width: '100%' }}>
              {Object.entries(stats.favoriteFeatureCounts)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([feature, count]) => (
                  <div key={feature} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{FEATURE_LABELS[feature] || feature}</span>
                    <Tag color="green">{count}</Tag>
                  </div>
                ))}
              {Object.keys(stats.favoriteFeatureCounts).length === 0 && (
                <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>No data available</div>
              )}
            </Space>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Top Features Needing Improvement">
            <Space direction="vertical" style={{ width: '100%' }}>
              {Object.entries(stats.improveFeatureCounts)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([feature, count]) => (
                  <div key={feature} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{FEATURE_LABELS[feature] || feature}</span>
                    <Tag color="orange">{count}</Tag>
                  </div>
                ))}
              {Object.keys(stats.improveFeatureCounts).length === 0 && (
                <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>No data available</div>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="Rating Distribution">
            <Row gutter={16}>
              {[5, 4, 3, 2, 1].map(rating => {
                const count = stats.ratingDistribution[rating] || 0;
                const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
                return (
                  <Col span={4} key={rating}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: 8 }}>
                        {count}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        {renderStars(rating)}
                      </div>
                      <div style={{ 
                        width: '100%', 
                        height: 8, 
                        background: '#f0f0f0', 
                        borderRadius: 4,
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${percentage}%`,
                          height: '100%',
                          background: rating >= 4 ? '#52c41a' : rating >= 3 ? '#faad14' : '#ff4d4f',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                        {percentage.toFixed(1)}%
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={surveys}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} surveys`
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Survey Details"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {selectedSurvey && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>User:</strong>{' '}
              {selectedSurvey.user_username || selectedSurvey.user_email || 'Unknown'} ({selectedSurvey.user_id.substring(0, 8)}...)
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Rating:</strong> {renderStars(selectedSurvey.rating)}
            </div>
            {selectedSurvey.nps_score != null && (
              <div style={{ marginBottom: 16 }}>
                <strong>NPS (0–10):</strong> {selectedSurvey.nps_score}
              </div>
            )}
            {selectedSurvey.usage_frequency && (
              <div style={{ marginBottom: 16 }}>
                <strong>Usage:</strong> {USAGE_LABELS[selectedSurvey.usage_frequency] || selectedSurvey.usage_frequency}
              </div>
            )}
            {selectedSurvey.device_type && (
              <div style={{ marginBottom: 16 }}>
                <strong>Device Type:</strong> {DEVICE_TYPE_LABELS[selectedSurvey.device_type] || selectedSurvey.device_type}
              </div>
            )}
            {selectedSurvey.completion_time_seconds && (
              <div style={{ marginBottom: 16 }}>
                <strong>Completion Time:</strong> {selectedSurvey.completion_time_seconds} seconds
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <strong>Favorite Features:</strong>
              <div style={{ marginTop: 8 }}>
                {selectedSurvey.favorite_features.length > 0 ? (
                  <Space size={[4, 4]} wrap>
                    {selectedSurvey.favorite_features.map(feature => (
                      <Tag key={feature} color="green">
                        {FEATURE_LABELS[feature] || feature}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <span style={{ color: '#999' }}>None selected</span>
                )}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Needs Improvement:</strong>
              <div style={{ marginTop: 8 }}>
                {selectedSurvey.least_favorite_features.length > 0 ? (
                  <Space size={[4, 4]} wrap>
                    {selectedSurvey.least_favorite_features.map(feature => (
                      <Tag key={feature} color="orange">
                        {FEATURE_LABELS[feature] || feature}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <span style={{ color: '#999' }}>None selected</span>
                )}
              </div>
            </div>
            {(selectedSurvey.main_value?.length ?? 0) > 0 && (
              <div style={{ marginBottom: 16 }}>
                <strong>Main value:</strong>
                <div style={{ marginTop: 8 }}>
                  <Space size={[4, 4]} wrap>
                    {selectedSurvey.main_value!.map(v => (
                      <Tag key={v}>{MAIN_VALUE_LABELS[v] || v}</Tag>
                    ))}
                  </Space>
                </div>
              </div>
            )}
            {(selectedSurvey.biggest_challenge?.length ?? 0) > 0 && (
              <div style={{ marginBottom: 16 }}>
                <strong>Biggest challenge:</strong>
                <div style={{ marginTop: 8 }}>
                  <Space size={[4, 4]} wrap>
                    {selectedSurvey.biggest_challenge!.map(c => (
                      <Tag key={c} color="orange">{CHALLENGE_LABELS[c] || c}</Tag>
                    ))}
                  </Space>
                </div>
              </div>
            )}
            {selectedSurvey.indispensable_feature && (
              <div style={{ marginBottom: 16 }}>
                <strong>Indispensable feature:</strong>
                <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                  {selectedSurvey.indispensable_feature}
                </div>
              </div>
            )}
            {selectedSurvey.additional_feedback && (
              <div style={{ marginBottom: 16 }}>
                <strong>Additional Feedback:</strong>
                <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                  {selectedSurvey.additional_feedback}
                </div>
              </div>
            )}
            <div>
              <strong>Submitted:</strong> {new Date(selectedSurvey.created_at).toLocaleString()}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Surveys;
